<template>
  <transition name="dialog-fade">
    <div class="message-dialog-overlay" v-if="visible">
      <div class="message-dialog-content">
        <div class="dialog-header">
          <h3 class="dialog-title" :class="dialogTypeClass">{{ title }}</h3>
          <span class="dialog-close-btn" @click="close">&times;</span>
        </div>
        <div class="dialog-body">
          <p class="dialog-message">{{ message }}</p>
        </div>
        <div class="dialog-footer">
          <button class="dialog-button cancel-button" @click="cancel" v-if="showCancelButton">
            {{ cancelButtonText }}
          </button>
          <button class="dialog-button confirm-button" @click="confirm">
            {{ confirmButtonText }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'MessageDialog',
  props: {
    title: {
      type: String,
      default: '提示'
    },
    message: {
      type: String,
      default: '这是一条提示信息。'
    },
    type: { // success, warning, error, info
      type: String,
      default: 'info'
    },
    showCancelButton: {
      type: Boolean,
      default: false
    },
    confirmButtonText: {
      type: String,
      default: '确定'
    },
    cancelButtonText: {
      type: String,
      default: '取消'
    }
  },
  data() {
    return {
      visible: false,
      resolve: null, // 用于存储 Promise 的 resolve 函数
      reject: null   // 用于存储 Promise 的 reject 函数
    };
  },
  computed: {
    dialogTypeClass() {
      return `dialog-type-${this.type}`;
    }
  },
  methods: {
    show() {
      this.visible = true;
      // 返回一个 Promise，调用方可以 await 这个 Promise 来获取结果
      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    close() {
      this.visible = false;
      // 当弹窗被关闭（例如点击X按钮或外部区域），但没有明确确认或取消时，我们通常会 reject
      if (this.reject) {
        this.reject(new Error('Dialog closed without confirmation/cancellation.'));
      }
    },
    confirm() {
      this.visible = false;
      if (this.resolve) {
        this.resolve(true); // 确认操作
      }
    },
    cancel() {
      this.visible = false;
      if (this.resolve) {
        this.resolve(false); // 取消操作
      }
    }
  }
};
</script>

<style scoped>
/* 弹窗覆盖层 */
.message-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6); /* 半透明黑色背景 */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999; /* 确保在最上层 */
}

/* 弹窗内容 */
.message-dialog-content {
  background-color: #2a2a47; /* 与卡片背景色一致 */
  border-radius: 10px;
  width: 90%;
  max-width: 350px; /* 最大宽度 */
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  overflow: hidden; /* 确保内容在圆角内 */
  display: flex;
  flex-direction: column;
}

/* 头部 */
.dialog-header {
  position: relative;
  padding: 15px 20px;
  border-bottom: 1px solid #3c3c5a;
  text-align: center;
  flex-shrink: 0;
}

.dialog-title {
  font-size: 18px;
  font-weight: bold;
  color: #fff;
  margin: 0;
}

/* 标题类型颜色 */
.dialog-type-info { color: #fff; } /* 信息 */
.dialog-type-success { color: #F39D1B; } /* 成功 */
.dialog-type-warning { color: #ffcc00; } /* 警告 */
.dialog-type-error { color: #ff5555; } /* 错误 */

.dialog-close-btn {
  position: absolute;
  top: 10px;
  right: 15px;
  font-size: 24px;
  color: #ccc;
  cursor: pointer;
  line-height: 1; /* 确保 X 居中 */
}

/* 内容体 */
.dialog-body {
  padding: 20px;
  text-align: center;
  flex-grow: 1; /* 允许内容区域填充 */
}

.dialog-message {
  font-size: 15px;
  color: #ccc;
  line-height: 1.5;
  margin: 0;
  word-break: break-word; /* 允许长单词换行 */
}

/* 底部按钮 */
.dialog-footer {
  display: flex;
  border-top: 1px solid #3c3c5a;
  flex-shrink: 0;
}

.dialog-button {
  flex: 1; /* 按钮平分空间 */
  padding: 15px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  border: none;
  background-color: transparent; /* 默认透明背景 */
  color: #fff;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.dialog-button:not(:last-child) {
  border-right: 1px solid #3c3c5a; /* 按钮之间分隔线 */
}

.dialog-button:active {
  background-color: rgba(0, 0, 0, 0.1);
}

/* 确认按钮颜色 */
.dialog-button.confirm-button {
  color: #F39D1B; /* 绿色确认按钮文字 */
}

/* 取消按钮颜色 */
.dialog-button.cancel-button {
  color: #ccc; /* 灰色取消按钮文字 */
}

/* 过渡动画 */
.dialog-fade-enter-active, .dialog-fade-leave-active {
  transition: opacity 0.3s ease;
}
.dialog-fade-enter, .dialog-fade-leave-to {
  opacity: 0;
}
.dialog-fade-enter-active .message-dialog-content,
.dialog-fade-leave-active .message-dialog-content {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.dialog-fade-enter .message-dialog-content,
.dialog-fade-leave-to .message-dialog-content {
  transform: translateY(-20px);
  opacity: 0;
}
</style>